import { Box, Chip, TextField, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface ApolloIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    apiKey?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: { apiKey: string }) => void;
  getToken: () => Promise<string>;
}

export default function ApolloIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
}: ApolloIntegrationFormProps) {
  const [apiKey, setApiKey] = useState(initialData?.apiKey || "");
  const [validating, setValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    isValid: boolean;
    message: string;
  } | null>(null);

  // Validate form
  useEffect(() => {
    const isValid = apiKey.trim() !== "" && validationStatus?.isValid === true;
    onValidationChange(isValid);
  }, [apiKey, validationStatus, onValidationChange]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      apiKey,
    });
  }, [apiKey, onDataChange]);

  const validateCredentials = async () => {
    if (!apiKey.trim()) {
      setValidationStatus({
        isValid: false,
        message: "Please enter an API key",
      });
      return;
    }

    setValidating(true);
    setValidationStatus(null);

    try {
      const currentToken = await getToken();

      // Validate API key
      await axios.post(
        `${hostname}/integrations/apollo/validate`,
        { apiKey },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      setValidationStatus({
        isValid: true,
        message: "API key is valid.",
      });
    } catch (error: any) {
      const errorMessage = parseError(error);
      setValidationStatus({
        isValid: false,
        message: errorMessage,
      });
    } finally {
      setValidating(false);
    }
  };

  const getValidationChip = () => {
    if (!validationStatus) {
      return null;
    }

    if (validationStatus.isValid) {
      return (
        <Box sx={{ mt: 1 }}>
          <Chip
            label={`✓ ${validationStatus.message}`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ mt: 1 }}>
          <Chip
            label={`✗ ${validationStatus.message}`}
            color="error"
            variant="outlined"
            size="small"
          />
        </Box>
      );
    }
  };

  return (
    <Box>
      <TextField
        sx={{ mt: 1 }}
        fullWidth
        type="password"
        value={apiKey}
        label="Apollo API Key"
        helperText="Your Apollo API key from your Apollo.io account settings"
        onChange={(e) => {
          setApiKey(e.target.value);
          // Reset validation when API key changes
          setValidationStatus(null);
        }}
        required
      />

      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <LoadingButton
          variant="outlined"
          size="small"
          onClick={validateCredentials}
          loading={validating}
          disabled={!apiKey.trim()}
        >
          Validate Credentials
        </LoadingButton>
      </Box>

      {getValidationChip()}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          To get your Apollo API key:
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          component="ol"
          sx={{ pl: 2 }}
        >
          <li>Go to your Apollo.io dashboard</li>
          <li>Navigate to Settings → Integrations → API</li>
          <li>Generate or copy your API key</li>
          <li>Paste it above and click "Validate Credentials"</li>
        </Typography>
      </Box>
    </Box>
  );
}
